import ModalWrapper from 'components/common/overlays/ModalWrapper';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import SingleBtn from 'components/common/elements/SingleBtn';
import useModal from 'hooks/useModal';
import { TDeleteRoundInfoFormValues } from 'types/form/professor';
import { DeleteRoundDefaultValues } from 'constants/professor';
import styles from 'styles/pages/modal/DeleteRound.module.scss';
import { useMutation } from 'react-query';
import { deleteRegisteredRound } from 'apis/professor';
import Input from 'components/common/forms/Input';
import { useState } from 'react';
import { isTCustomAxiosError } from 'apis/config/axios';
import { postPositionWithWord } from 'utils/postPosition';
import { TDeleteRegisteredRoundParams } from 'types/apis/professor';

type TProps = {
  chartId: number;
  chartName: string;
  refetchRound: () => void;
};

const DeleteRound = ({ chartId, chartName, refetchRound }: TProps) => {
  const { openModal, closeModal } = useModal();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const methods = useForm<TDeleteRoundInfoFormValues>({
    defaultValues: DeleteRoundDefaultValues,
  });

  const password = useWatch({
    control: methods.control,
    name: 'password',
  });

  const { mutate: deleteRegisteredRoundMutation } = useMutation(
    ({
      chartId,
      data,
    }: {
      chartId: number;
      data?: TDeleteRegisteredRoundParams;
    }) => deleteRegisteredRound(chartId, data),
    {
      onSuccess: () => {
        closeModal();
        openModal({
          title: '차트가 삭제되었습니다.',
          text: `${postPositionWithWord({
            format: ['이', '가'],
            word: chartName || '',
          })} 삭제되었습니다.`,
          onSubmit: () => refetchRound(),
        });
      },
      onError: (error) => {
        if (isTCustomAxiosError(error)) {
          if (
            error.code === 400 &&
            error.message === '비밀번호가 일치하지 않습니다.'
          ) {
            setErrorMsg('비밀번호가 일치하지 않습니다. 다시 입력해 주세요.');
          } else if (error.code === 820) {
            openModal({
              text: '성적을 마감한 차트는 삭제할 수 없습니다.',
              onSubmit: closeModal,
            });
          } else if (error.code === 417) {
            setErrorMsg('비밀번호가 일치하지 않습니다. 다시 입력해 주세요.');
          }
        }
      },
    }
  );

  const onClickDelete = (chartId: number) => {
    const data = password ? { password } : undefined;
    deleteRegisteredRoundMutation({
      chartId,
      data,
    });
  };

  return (
    <ModalWrapper>
      <FormProvider {...methods}>
        <form>
          <div className="flex justify_between items_center">
            <h2 className={styles.title}>차트 삭제하기</h2>
            <button type="button" onClick={closeModal}>
              <CloseIcon fill={STYLES_VARIABLES.GRAY_07} />
            </button>
          </div>
          <p className={styles.sub_text}>
            {`[${chartName}] 차트를 삭제하시겠습니까?`}
          </p>
          <p className={styles.text_wrapper}>
            차트를 삭제하면 “등록 회차” 목록에서 즉시 사라지고 데이터 복구가
            불가합니다. 삭제 동의하시면 비밀번호를 입력해 주세요.
          </p>

          <Input
            name="password"
            inputType="password"
            placeholder="비밀번호를 입력해 주세요."
            inputStyle={{
              marginTop: '16px',
            }}
          />

          {errorMsg ? (
            <div className={styles.height}>
              <p className="error_text">{errorMsg}</p>
            </div>
          ) : (
            <div className={styles.height}></div>
          )}
          <div className="modal_btn_wrapper mt8">
            <SingleBtn
              btnText="취소"
              hasOutline={true}
              btnStyle={{
                width: '100%',
              }}
              onClick={closeModal}
            />
            <SingleBtn
              btnText="삭제"
              isBtnActive={true}
              btnStyle={{
                width: '100%',
              }}
              onClick={() => onClickDelete(chartId)}
            />
          </div>
        </form>
      </FormProvider>
    </ModalWrapper>
  );
};

export default DeleteRound;
